import React, {
	FC,
	forwardRef,
	ReactNode,
	Ref,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react'
import { useFocusWithin } from '@mantine/hooks'
import cn from '../../lib/utils/cn'
import { useUserDateFormat } from '../../hooks/useUserDateFormat'
import { PlayArrow } from '@mui/icons-material'
import AppContext from '../../context/appContext'

interface InputItem {
	id: string
	label: string
	[key: string]: any
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	label?: string | ReactNode
	wrapperClassName?: string
	labelClassName?: string
	inputClassName?: string
	errorClassName?: string
	itemsWrapperClassName?: string
	itemClassName?: string
	leftIconWrapperClassName?: string
	rightIconWrapperClassName?: string
	error?: string
	leftIcon?: ReactNode | null
	rightIcon?: ReactNode | null
	witArrowRightIcon?: boolean
	placeholder?: string
	type?: string
	hideValue?: boolean
	value?: string | number
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
	onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void
	onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => void
	onEnterClick?: (e: React.KeyboardEvent<HTMLInputElement>) => void
	withItems?: boolean
	items?: InputItem[]
	emptyItemsMessage?: string | ReactNode
	onItemClick?: (item: InputItem) => void
	loading?: boolean
	required?: boolean
	min?: number | string
}

function mergeRefs<T = any>(...refs: (Ref<T> | undefined)[]) {
	return (value: T) => {
		refs.forEach((ref) => {
			if (typeof ref === 'function') {
				ref(value)
			} else if (ref != null && typeof ref === 'object') {
				// @ts-expect-error: Assigning value to ref.current for compatibility with both function and object refs
				ref.current = value
			}
		})
	}
}

const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			label,
			wrapperClassName,
			labelClassName = '',
			inputClassName = '',
			errorClassName = '',
			itemsWrapperClassName = '',
			itemClassName = '',
			leftIconWrapperClassName,
			rightIconWrapperClassName,
			leftIcon = null,
			rightIcon = null,
			witArrowRightIcon = false,
			placeholder = '',
			type = 'text',
			hideValue = false,
			error,
			value,
			onChange,
			onBlur,
			onFocus,
			onItemClick,
			onEnterClick,
			withItems,
			items,
			emptyItemsMessage,
			loading,
			required,
			min,
			style,
			...inputProps
		},
		ref,
	) => {
		const { isApp } = useContext(AppContext)
		const { formatDate } = useUserDateFormat()

		const { ref: focusRef, focused } = useFocusWithin()
		const dateInputRef = useRef<HTMLInputElement>(null)
		const isDateType = type === 'date'
		const displayValue = isDateType
			? value
				? formatDate(String(value))
				: 'DD/MM/YYYY'
			: value

		const handleWrapperClick = () => {
			if (isDateType) {
				dateInputRef.current?.showPicker?.()
			}
		}
		const [isOpen, setIsOpen] = useState(false)

		useEffect(() => {
			setIsOpen(focused)
		}, [focused])

		const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter' && focused && onEnterClick) {
				e.preventDefault()
				onEnterClick(e)
			}
		}

		return (
			<div
				className={cn(
					'relative flex flex-col w-auto h-fit',
					isDateType && 'hover:cursor-pointer',
					wrapperClassName,
				)}
			>
				{label ? (
					<label
						className={cn(
							'text-text-main font-semibold text-base mb-2 flex flex-col gap-1 dark:text-text-main-dark',
							labelClassName,
						)}
					>
						<span className="text-inherit">
							{label}
							{required && <span className="text-accent"> *</span>}
						</span>
						{!!error && (
							<span
								className={cn(
									'text-negative text-sm font-normal break-words w-full line-clamp-2',
									errorClassName,
								)}
							>
								{error}
							</span>
						)}
					</label>
				) : (
					error && (
						<span
							className={cn(
								'text-negative text-sm font-normal break-words w-full line-clamp-2 mb-1',
								errorClassName,
							)}
						>
							{error}
						</span>
					)
				)}
				<div
					className="relative flex items-center h-full w-full"
					onClick={handleWrapperClick}
				>
					{leftIcon && (
						<div
							className={cn(
								'absolute z-10 left-4 flex items-center justify-center',
								leftIconWrapperClassName,
							)}
						>
							{leftIcon}
						</div>
					)}

					<input
						ref={mergeRefs(ref, focusRef)}
						readOnly={isDateType}
						type={isDateType ? 'text' : type}
						placeholder={placeholder}
						value={displayValue}
						onChange={onChange}
						onFocus={onFocus}
						onBlur={onBlur}
						onKeyDown={handleKeyDown}
						min={min}
						style={{
							outline: 'none',
							boxShadow: 'none',
							...style,
							...(hideValue && { WebkitTextSecurity: 'disc' }),
						}}
						className={cn(
							'border border-stroke-dark dark:border-stroke-light-dark bg-background-input dark:bg-background-input-dark rounded-lg focus:ring-stroke-light-hover focus:outline-none h-full w-full placeholder:text-stroke-dark box-border text-base md:text-sm text-text-main dark:text-text-main-dark',
							leftIcon ? 'pl-11' : 'pl-4',
							rightIcon && 'pr-10',
							withItems && focused && 'rounded-b-none',
							isDateType && 'hover:cursor-pointer',
							inputClassName,
							isApp && 'text-base',
							error && 'border-negative dark:border-negative-dark',
						)}
						{...(isDateType ? {} : inputProps)}
					/>

					{isDateType && (
						<input
							ref={dateInputRef}
							type="date"
							value={value as string}
							onChange={onChange}
							min={min}
							style={style}
							className="absolute top-0 left-0 w-full h-full opacity-0 pointer-events-none"
							{...inputProps}
						/>
					)}

					{Boolean(witArrowRightIcon || rightIcon) && (
						<div
							className={cn(
								'absolute z-10 right-2 flex items-center justify-center',
								rightIconWrapperClassName,
							)}
						>
							{witArrowRightIcon ? (
								<PlayArrow
									className={cn(
										'w-4 h-4 fill-stroke-dark',
										isOpen && withItems ? '-rotate-90' : 'rotate-90',
									)}
								/>
							) : (
								rightIcon
							)}
						</div>
					)}
				</div>
				{!!withItems && isOpen && (
					<div
						onMouseDown={(e) => e?.stopPropagation()}
						className={cn(
							'fixed z-50 flex-1 max-h-36 h-fit overflow-y-auto overflow-x-hidden rounded-b-lg bg-background-input dark:bg-background-dark border border-stroke-light dark:border-stroke-light-dark border-t-1 shadow-lg box-border',
							itemsWrapperClassName,
						)}
						style={{
							width: focusRef.current?.offsetWidth,
							top: focusRef.current?.getBoundingClientRect().bottom,
							left: focusRef.current?.getBoundingClientRect().left,
						}}
					>
						{items?.length ? (
							items?.map((item, index) => (
								<p
									onMouseDown={(e) => e?.preventDefault()}
									onClick={(e) => {
										e.stopPropagation()
										if (item?.disabled) return
										onItemClick && onItemClick(item)
										setIsOpen(false)
										focusRef?.current?.blur()
									}}
									key={item?.id || index}
									className={cn(
										'duration-default h-6 hover:bg-background-light-hover w-full p-2.5 cursor-pointer ellipsis border-box text-text-main dark:text-text-main-dark font-medium dark:hover:bg-background-light-dark dark:hover:text-text-main-dark',
										itemClassName,
										item?.disabled && 'opacity-30 pointer-events-none',
									)}
								>
									{item?.label}
								</p>
							))
						) : (
							<div
								className="text-center m-5 text-sm text-text-secondary max-w-fit mx-auto"
								onMouseDown={(e) => e?.preventDefault()}
							>
								{loading
									? 'Loading...'
									: (emptyItemsMessage ?? 'There are no items to add')}
							</div>
						)}
					</div>
				)}
			</div>
		)
	},
)

Input.displayName = 'Input'

export default Input
